import {
  sendEmailVerification,
  User,
  applyActionCode,
  confirmPasswordReset,
  checkActionCode
} from 'firebase/auth';
import { auth, db, functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import EmailService from './emailService';
import { doc, setDoc, getDoc, deleteDoc, Timestamp } from 'firebase/firestore';

// Passcode verification service
export class PasscodeVerificationService {
  // Generate a 6-digit passcode
  private static generatePasscode(): string {
    const buf = new Uint32Array(1);
    crypto.getRandomValues(buf);
    return (100000 + (buf[0] % 900000)).toString();
  }

  // Store passcode in Firestore with expiration (reuse existing unexpired code)
  private static async storePasscode(userId: string, passcode: string, type: 'email_verification' | 'password_reset', forceNew: boolean = false): Promise<string> {
    const passcodeRef = doc(db, 'verification_codes', `${userId}_${type}`);
    const existing = await getDoc(passcodeRef);
    const now = new Date();
    const expiresAt = new Date();
    expiresAt.setMinutes(expiresAt.getMinutes() + 10); // 10 minutes expiration

    if (forceNew) {
      await setDoc(passcodeRef, {
        passcode,
        type,
        userId,
        createdAt: Timestamp.now(),
        expiresAt: Timestamp.fromDate(expiresAt),
        attempts: 0,
        maxAttempts: 3
      });
      return passcode;
    }

    if (existing.exists()) {
      const data = existing.data();
      const existingExpiry = data.expiresAt?.toDate?.() || new Date(data.expiresAt);
      if (existingExpiry && existingExpiry > now && data.attempts < (data.maxAttempts ?? 3)) {
        // Reuse existing code to avoid duplicates/mismatch
        console.log('♻️ Reusing existing unexpired verification code');
        return data.passcode as string;
      }
    }

    await setDoc(passcodeRef, {
      passcode,
      type,
      userId,
      createdAt: Timestamp.now(),
      expiresAt: Timestamp.fromDate(expiresAt),
      attempts: 0,
      maxAttempts: 3
    });
    return passcode;
  }

  // Verify passcode from Firestore
  private static async verifyPasscode(userId: string, passcode: string, type: 'email_verification' | 'password_reset'): Promise<{ valid: boolean; error?: string }> {
    try {
      const passcodeRef = doc(db, 'verification_codes', `${userId}_${type}`);
      const passcodeDoc = await getDoc(passcodeRef);

      // SECURITY: Do not log actual passcode value
      console.log('🔍 Checking passcode in Firestore:', {
        userId,
        type,
        passcodeLength: passcode.length,
        documentId: `${userId}_${type}`,
        exists: passcodeDoc.exists()
      });

      if (!passcodeDoc.exists()) {
        console.log('❌ No verification code document found');
        return { valid: false, error: 'No verification code found. Please request a new one.' };
      }

      const data = passcodeDoc.data();
      // SECURITY: Do not log actual passcode values
      console.log('📋 Checking passcode verification:', {
        passcodeLength: passcode.length,
        attempts: data.attempts,
        maxAttempts: data.maxAttempts,
        expiresAt: data.expiresAt?.toDate()
      });

      if (process.env.NODE_ENV !== 'production') {
        console.log('🧪 [DEV] Stored verification passcode for debugging:', data.passcode);
      }

      const now = new Date();
      const expiresAt = data.expiresAt.toDate();

      // Check if expired
      if (now > expiresAt) {
        console.log('⏰ Passcode expired');
        await deleteDoc(passcodeRef);
        return { valid: false, error: 'Verification code has expired. Please request a new one.' };
      }

      // Check attempts
      if (data.attempts >= data.maxAttempts) {
        console.log('🚫 Too many attempts');
        await deleteDoc(passcodeRef);
        return { valid: false, error: 'Too many incorrect attempts. Please request a new code.' };
      }

      // SECURITY: Do not log actual passcode values
      console.log('🔐 Verifying passcode...');

      if (data.passcode !== passcode) {
        // Increment attempts
        await setDoc(passcodeRef, {
          ...data,
          attempts: data.attempts + 1
        }, { merge: true });

        const remainingAttempts = data.maxAttempts - (data.attempts + 1);
        console.log('❌ Passcode mismatch, attempts remaining:', remainingAttempts);
        return {
          valid: false,
          error: `Incorrect code. ${remainingAttempts} attempts remaining.`
        };
      }

      // Success - delete the passcode
      console.log('✅ Passcode verified successfully');
      await deleteDoc(passcodeRef);
      return { valid: true };
    } catch (error) {
      console.error('Error verifying passcode:', error);
      return { valid: false, error: 'An error occurred while verifying the code.' };
    }
  }

  // Send email verification passcode
  static async sendEmailVerificationPasscode(user: User, forceNew: boolean = false): Promise<{ success: boolean; error?: string }> {
    try {
      let passcode = this.generatePasscode();

      // SECURITY: Do not log passcode to console
      console.log('🔑 Generated verification passcode (not logged for security)');
      console.log('👤 User info:', {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName
      });

      passcode = await this.storePasscode(user.uid, passcode, 'email_verification', forceNew);

      console.log('📧 Sending verification email to:', user.email);

      // Use the new email service
      const result = await EmailService.sendVerificationEmail(
        user.email || '',
        user.displayName || 'User',
        passcode
      );

      if (result.success) {
        console.log('✅ Verification email sent successfully');
        return { success: true };
      } else {
        console.error('❌ Failed to send verification email:', result.error);
        return {
          success: false,
          error: result.error || 'Failed to send verification email'
        };
      }
    } catch (error) {
      console.error('Error sending email verification passcode:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to send verification code'
      };
    }
  }

  // Legacy method for fallback (using default templates)
  private static async sendEmailVerificationPasscodeLegacy(user: User, passcode: string): Promise<{ success: boolean; error?: string }> {
    try {
      // Use the new email service for fallback
      const result = await EmailService.sendVerificationEmail(
        user.email || '',
        user.displayName || 'User',
        passcode
      );

      return result;
    } catch (error) {
      console.error('Legacy email verification failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to send verification email'
      };
    }
  }

  // Verify email with passcode
  static async verifyEmailWithPasscode(user: User, passcode: string): Promise<{ success: boolean; error?: string }> {
    try {
      const verification = await this.verifyPasscode(user.uid, passcode, 'email_verification');

      if (!verification.valid) {
        return { success: false, error: verification.error };
      }

      // If passcode is valid, mark email as verified in our custom system
      await PasscodeVerificationService.markEmailAsVerified(user.uid, user.email || '');

      console.log('✅ Email marked as verified in custom system');

      return { success: true };
    } catch (error) {
      console.error('Error verifying email with passcode:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to verify email'
      };
    }
  }

  // Mark email as verified in our custom system
  static async markEmailAsVerified(userId: string, email: string, verificationMethod: string = 'passcode'): Promise<void> {
    const verificationStatusRef = doc(db, 'user_verification_status', userId);
    await setDoc(verificationStatusRef, {
      email,
      emailVerified: true,
      verifiedAt: Timestamp.now(),
      verificationMethod
    }, { merge: true });
  }

  // Check if email is verified in our custom system
  static async isEmailVerified(userId: string): Promise<boolean> {
    try {
      const verificationStatusRef = doc(db, 'user_verification_status', userId);
      const docSnap = await getDoc(verificationStatusRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        return data.emailVerified === true;
      }

      return false;
    } catch (error) {
      console.error('Error checking email verification status:', error);
      return false;
    }
  }

  // Extract a user-facing message from a callable error
  private static callableErrorMessage(error: unknown, fallback: string): string {
    const e = error as { message?: string } | undefined;
    return e?.message ? e.message.replace(/^(FirebaseError: )?/, "") : fallback;
  }

  // Send password reset passcode.
  // SECURITY: The code is generated, stored and emailed server-side by the
  // requestPasswordReset Cloud Function; the client never sees or stores it.
  static async sendPasswordResetPasscode(email: string, _userName?: string): Promise<{ success: boolean; error?: string }> {
    try {
      const requestReset = httpsCallable(functions, "requestPasswordReset");
      await requestReset({ email: email.trim() });
      return { success: true };
    } catch (error) {
      console.error("Error sending password reset passcode:", error);
      return {
        success: false,
        error: this.callableErrorMessage(error, "Failed to send reset code")
      };
    }
  }

  // Verify password reset passcode (server-side; does not consume the code)
  static async verifyPasswordResetPasscode(email: string, passcode: string): Promise<{ valid: boolean; error?: string }> {
    try {
      const verifyCode = httpsCallable(functions, "verifyPasswordResetCode");
      await verifyCode({ email: email.trim(), passcode });
      return { valid: true };
    } catch (error) {
      console.error("Error verifying password reset passcode:", error);
      return {
        valid: false,
        error: this.callableErrorMessage(error, "An error occurred while verifying the code.")
      };
    }
  }
}

export default PasscodeVerificationService;
